/**
 * NetworkStatus — Offline/Online banner + hook
 * Shows top banner when offline. Shows "Back online" toast on reconnect.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
      }
    };
    const goOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return { isOnline, wasOffline: wasOfflineRef.current };
}

export const NetworkBanner = () => {
  const { isOnline } = useNetworkStatus();
  const [showReconnect, setShowReconnect] = useState(false);
  const wasOffline = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      setShowReconnect(false);
    } else if (wasOffline.current) {
      wasOffline.current = false;
      setShowReconnect(true);
      const timer = setTimeout(() => setShowReconnect(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  if (isOnline && !showReconnect) return null;

  return (
    <div
      data-testid="network-banner"
      className={`fixed top-0 left-0 right-0 z-[100] px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium transition-all duration-300 ${
        isOnline
          ? 'bg-emerald-500 text-white'
          : 'bg-amber-500 text-white'
      }`}
    >
      {isOnline ? (
        <>
          <Wifi className="w-4 h-4" />
          <span>{t('backOnline')}</span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4" />
          <span>{t('youAreOffline')}</span>
        </>
      )}
    </div>
  );
};
